import { Client } from "@xmpp/client"
import { fetchArchivePage, MAMQueryOptions } from "./xeps/MAM"
import MAMStanzaProcessor, { AccumulatedData } from "./xeps/MAMStanzaProcessor"
import { isMucJid } from "@/jidUtils"

export type HistoryDirection = "older" | "newer"
export const HISTORY_PAGE_SIZE = 40

// Search timestamps are microseconds. Keep the precision of the archive boundary.
export const searchTimestampToISO = (timestamp: number) =>
  new Date(Math.floor(timestamp / 1000))
    .toISOString()
    .replace(/(\d{3})Z$/, `$1${String(timestamp % 1000).padStart(3, "0")}Z`)

export async function fetchSearchHistoryPage({
  client,
  chatJid,
  direction,
  cursor,
  timestamp,
  fromStart = false,
  endTimestamp,
  pageSize = HISTORY_PAGE_SIZE,
}: {
  client: Client
  chatJid: string
  direction: HistoryDirection
  cursor?: string
  timestamp?: number
  fromStart?: boolean
  endTimestamp?: number
  pageSize?: number
}) {
  if (!client.jid) throw new Error("XMPP client is not connected")
  if (
    !cursor &&
    timestamp === undefined &&
    !(fromStart && direction === "newer")
  )
    throw new Error("Missing archive boundary")
  const older = direction === "older"
  const stamp =
    timestamp === undefined ? undefined : searchTimestampToISO(timestamp)
  const options: MAMQueryOptions = {
    ...(isMucJid(chatJid) ? { toJid: chatJid } : { withJid: chatJid }),
    max: String(Math.min(HISTORY_PAGE_SIZE, Math.max(1, pageSize))),
    ...(cursor
      ? older
        ? { beforeId: cursor }
        : { afterId: cursor }
      : fromStart && !older
        ? {}
        : older
          ? { before: true, beforeStamp: stamp }
          : { afterStamp: stamp }),
    ...(endTimestamp === undefined
      ? {}
      : { beforeStamp: searchTimestampToISO(endTimestamp) }),
  }
  // One bounded archive page, including pages containing only metadata/receipts.
  // Pagination follows RSM IDs, never the first/last visible message.
  const { accumulator, result } = await fetchArchivePage(client, options)
  const fin = result.getChild("fin", "urn:xmpp:mam:2")
  if (!fin) throw new Error("Missing archive completion response")
  const set = fin.getChild("set", "http://jabber.org/protocol/rsm")
  const nextCursor = set?.getChildText(older ? "first" : "last") || undefined
  const hasMore = !["true", "1"].includes(fin.attrs.complete)
  if (hasMore && (!nextCursor || nextCursor === cursor)) {
    throw new Error("Archive cursor did not advance")
  }
  const data: AccumulatedData = {
    messagesAccumulator: [],
    markersAccumulator: [],
    reactionsAccumulator: [],
    messageFileSharesAccumulator: [],
    messageRetractionsAccumulator: [],
    extractedMetadataAccumulator: [],
    documentDriverContextAccumulator: [],
    deliveryOrdersAccumulator: [],
    documentLinksAccumulator: [],
    routeUpdatesAccumulator: [],
  }
  MAMStanzaProcessor.accumulatePages(
    data.messagesAccumulator,
    data.markersAccumulator,
    data.reactionsAccumulator,
    data.messageFileSharesAccumulator,
    data.messageRetractionsAccumulator,
    data.extractedMetadataAccumulator,
    data.documentDriverContextAccumulator,
    data.deliveryOrdersAccumulator,
    data.documentLinksAccumulator,
    data.routeUpdatesAccumulator,
    client,
  )(accumulator)
  return {
    cursor: nextCursor,
    firstCursor: set?.getChildText("first") || undefined,
    lastCursor: set?.getChildText("last") || undefined,
    hasMore,
    payload: MAMStanzaProcessor.processAccumulatedData(
      data,
      older ? "desc" : "asc",
      false,
      client.jid.toString(),
      older ? "prepend" : "append",
    ),
  }
}

// Corrections can refer to a message far outside the window around the edit.
// Use opaque archive IDs to jump there, never walk the archive back to it.
export async function fetchSearchHistoryAroundId({
  client,
  chatJid,
  messageId,
}: {
  client: Client
  chatJid: string
  messageId: string
}) {
  const older = await fetchSearchHistoryPage({
    client,
    chatJid,
    direction: "older",
    cursor: messageId,
  })
  // The page immediately after the nearest preceding event includes the target
  // itself. This also works for the very first archived event.
  const newer = await fetchSearchHistoryPage({
    client,
    chatJid,
    direction: "newer",
    cursor: older.lastCursor,
    fromStart: !older.lastCursor,
  })
  return { older, newer }
}
